<template>
  <section class="explorar" aria-label="Explorar movimientos">
    <!-- Barra de filtros -->
    <div class="card border-0 shadow-sm filtros mb-3">
      <div class="card-body p-2 p-md-3">
        <div class="d-flex flex-wrap align-items-center gap-2">
          <div class="btn-group modo" role="group" aria-label="Tipo de movimiento">
            <button type="button" class="btn btn-sm" :class="f.tipo === 'gasto' ? 'btn-gasto' : 'btn-outline-secondary'" :aria-pressed="f.tipo === 'gasto'" @click="setTipo('gasto')">
              <i class="bi bi-arrow-up-right me-1" aria-hidden="true"></i>Gastos
            </button>
            <button type="button" class="btn btn-sm" :class="f.tipo === 'ingreso' ? 'btn-ingreso' : 'btn-outline-secondary'" :aria-pressed="f.tipo === 'ingreso'" @click="setTipo('ingreso')">
              <i class="bi bi-arrow-down-left me-1" aria-hidden="true"></i>Ingresos
            </button>
          </div>

          <div class="presets d-flex gap-1" role="group" aria-label="Periodo">
            <button v-for="p in PRESETS" :key="p.id" type="button" class="btn btn-sm chip" :class="{ activo: f.preset === p.id }" @click="aplicarPreset(p.id)">
              {{ p.label }}
            </button>
          </div>

          <div class="d-flex align-items-center gap-1 ms-md-auto rango">
            <button type="button" class="btn btn-sm btn-light" aria-label="Periodo anterior" @click="desplazar(-1)"><i class="bi bi-chevron-left"></i></button>
            <input v-model="f.desde" type="date" class="form-control form-control-sm" aria-label="Desde" :max="f.hasta" @change="manual" />
            <span class="text-muted small">–</span>
            <input v-model="f.hasta" type="date" class="form-control form-control-sm" aria-label="Hasta" :min="f.desde" @change="manual" />
            <button type="button" class="btn btn-sm btn-light" aria-label="Periodo siguiente" @click="desplazar(1)"><i class="bi bi-chevron-right"></i></button>
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
          <div class="position-relative flex-grow-1 buscador">
            <i class="bi bi-search position-absolute text-muted" aria-hidden="true"></i>
            <input v-model.trim="qInput" type="search" class="form-control form-control-sm ps-4" placeholder="Buscar comercio o nota…" aria-label="Buscar" @keyup.enter="aplicarBusqueda" @search="aplicarBusqueda" />
          </div>
          <details class="desplegable" :open="cuentasAbierto" @toggle="cuentasAbierto = $event.target.open">
            <summary class="btn btn-sm btn-light">
              <i class="bi bi-bank me-1" aria-hidden="true"></i>{{ f.cuentas.length ? `${f.cuentas.length} cuenta${f.cuentas.length > 1 ? 's' : ''}` : 'Todas las cuentas' }}
            </summary>
            <div class="desplegable-menu shadow">
              <div v-for="c in cuentasVisibles" :key="c.id" class="form-check">
                <input :id="`ex-c-${c.id}`" class="form-check-input" type="checkbox" :value="c.id" :checked="f.cuentas.includes(c.id)" @change="toggleCuenta(c.id)" />
                <label class="form-check-label small" :for="`ex-c-${c.id}`"><span class="dot" :style="{ background: c.color || '#adb5bd' }"></span>{{ c.nombre }}</label>
              </div>
              <p class="small text-muted mb-0 mt-1">Sin elegir, se excluyen las cuentas «fuera de estadísticas».</p>
            </div>
          </details>
          <div class="d-flex align-items-center gap-1 importes">
            <input v-model.number="f.min" type="number" min="0" step="1" class="form-control form-control-sm" placeholder="Mín €" aria-label="Importe mínimo" @change="cargar" />
            <input v-model.number="f.max" type="number" min="0" step="1" class="form-control form-control-sm" placeholder="Máx €" aria-label="Importe máximo" @change="cargar" />
          </div>
        </div>

        <div v-if="chipsActivos.length" class="d-flex flex-wrap gap-1 mt-2" aria-label="Filtros activos">
          <span v-for="c in chipsActivos" :key="c.key" class="badge chip-activo">
            <span v-if="c.color" class="dot" :style="{ background: c.color }"></span>{{ c.label }}
            <button type="button" class="btn-close btn-close-sm ms-1" :aria-label="`Quitar filtro ${c.label}`" @click="c.quitar()"></button>
          </span>
          <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="limpiar">Limpiar filtros</button>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger small">{{ error }}</div>

    <div v-if="!datos && cargando" class="text-center text-muted py-5" role="status">
      <div class="spinner-border text-primary" aria-hidden="true"></div>
    </div>

    <template v-if="datos">
      <div :class="{ 'opacity-50': cargando }" class="transicion">
        <!-- Hero -->
        <div class="row g-3 mb-3">
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100 hero" :class="`hero-${datos.tipo}`">
              <div class="card-body">
                <div class="hero-label">{{ datos.tipo === 'gasto' ? 'Gastado' : 'Ingresado' }} · {{ rangoTexto }}</div>
                <div class="hero-total">{{ euros(datos.totales.total) }}</div>
                <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <span v-if="datos.totales.variacionPct != null" class="badge variacion" :class="claseVariacion">
                    <i :class="['bi', datos.totales.variacionPct >= 0 ? 'bi-arrow-up' : 'bi-arrow-down']" aria-hidden="true"></i>
                    {{ Math.abs(datos.totales.variacionPct) }}% vs periodo anterior
                  </span>
                  <span class="small text-muted">antes {{ euros(datos.totales.totalAnterior) }} ({{ rangoAnteriorTexto }})</span>
                </div>
                <p class="mb-0 resumen"><i class="bi bi-stars me-1" aria-hidden="true"></i>{{ datos.resumen }}</p>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="row g-3 h-100">
              <div class="col-6"><div class="card border-0 shadow-sm h-100 mini"><div class="card-body">
                <div class="mini-label">Movimientos</div><div class="mini-valor">{{ datos.totales.n }}</div>
                <div class="mini-sub">antes {{ datos.totales.nAnterior }}</div>
              </div></div></div>
              <div class="col-6"><div class="card border-0 shadow-sm h-100 mini"><div class="card-body">
                <div class="mini-label">Ticket medio</div><div class="mini-valor">{{ euros(datos.totales.ticketMedio) }}</div>
                <div class="mini-sub">por movimiento</div>
              </div></div></div>
              <div class="col-6"><div class="card border-0 shadow-sm h-100 mini"><div class="card-body">
                <div class="mini-label">Media diaria</div><div class="mini-valor">{{ euros(datos.totales.mediaDiaria) }}</div>
                <div class="mini-sub">{{ datos.rango.dias }} días</div>
              </div></div></div>
              <div class="col-6"><div class="card border-0 shadow-sm h-100 mini"><div class="card-body">
                <div class="mini-label">Balance del periodo</div>
                <div class="mini-valor" :class="datos.totales.balance >= 0 ? 'text-success' : 'text-danger'">
                  {{ datos.totales.balance >= 0 ? '+' : '' }}{{ euros(datos.totales.balance) }}
                </div>
                <div class="mini-sub">ingresos {{ euros(datos.totales.ingresos) }} · gastos {{ euros(datos.totales.gastos) }}</div>
                <div v-if="datos.totales.traspasosExcluidos" class="mini-sub" :title="`Movimientos entre tus propias cuentas: no son ni gasto ni ingreso`">
                  <i class="bi bi-arrow-left-right me-1" aria-hidden="true"></i>{{ datos.totales.traspasosExcluidos }} traspasos entre cuentas excluidos ({{ euros(datos.totales.traspasosImporte) }})
                </div>
              </div></div></div>
            </div>
          </div>
        </div>

        <!-- Evolución -->
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-baseline">
              <h2 class="h6 mb-0">Evolución {{ { dia: 'diaria', semana: 'semanal', mes: 'mensual' }[datos.granularidad] }}</h2>
              <span class="small text-muted d-none d-md-inline">Pulsa una barra para ver ese {{ { dia: 'día', semana: 'semana', mes: 'mes' }[datos.granularidad] }}</span>
            </div>
            <div class="chart-box">
              <Bar :data="chartData" :options="chartOptions" role="img" :aria-label="`Evolución de ${datos.tipo === 'gasto' ? 'gastos' : 'ingresos'} frente al periodo anterior`" />
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <!-- Categorías -->
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <h2 class="h6">Por categoría</h2>
                <p class="small text-muted">Pulsa para filtrar; puedes combinar varias.</p>
                <p v-if="!datos.porCategoria.length" class="text-muted small mb-0">Sin datos en este periodo.</p>
                <ul class="list-unstyled mb-0 cat-lista">
                  <li v-for="c in datos.porCategoria.slice(0, verTodasCats ? 999 : 10)" :key="c.id">
                    <button type="button" class="cat-fila" :class="{ seleccionada: c.seleccionada, apagada: hayFiltroCat && !c.seleccionada }" :aria-pressed="c.seleccionada" @click="toggleCategoria(c.id)">
                      <span class="d-flex justify-content-between small">
                        <span class="text-truncate"><span class="dot" :style="{ background: c.color || '#adb5bd' }"></span>{{ c.nombre }} <span class="text-muted">· {{ c.n }}</span></span>
                        <span class="text-nowrap">
                          <strong>{{ euros(c.total) }}</strong>
                          <span class="text-muted ms-1">{{ c.pct }}%</span>
                          <span v-if="c.variacionPct != null && c.variacionPct !== 0" class="ms-1" :class="claseDelta(c.variacionPct)">
                            <i :class="['bi', c.variacionPct > 0 ? 'bi-caret-up-fill' : 'bi-caret-down-fill']" aria-hidden="true"></i>{{ Math.abs(c.variacionPct) }}%
                          </span>
                        </span>
                      </span>
                      <span class="barra-fondo"><span class="barra" :style="{ width: `${Math.max(c.pct, 0.5)}%`, background: colorModo }"></span></span>
                    </button>
                  </li>
                </ul>
                <button v-if="datos.porCategoria.length > 10" type="button" class="btn btn-link btn-sm p-0 mt-2" @click="verTodasCats = !verTodasCats">
                  {{ verTodasCats ? 'Ver menos' : `Ver las ${datos.porCategoria.length}` }}
                </button>
              </div>
            </div>
          </div>

          <!-- Calendario + día de la semana -->
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <h2 class="h6">Calendario</h2>
                <template v-if="calendario">
                  <div class="heatmap-scroll">
                    <div class="heatmap" :style="{ gridTemplateColumns: `1.6rem repeat(${calendario.semanas.length}, 1fr)` }" role="grid" aria-label="Importe por día">
                      <template v-for="(fila, d) in 7" :key="`f${d}`">
                        <span class="hm-dia">{{ ['L', '', 'X', '', 'V', '', 'D'][d] }}</span>
                        <template v-for="(sem, w) in calendario.semanas" :key="`c${w}-${d}`">
                          <button
                            v-if="sem[d]"
                            type="button"
                            class="hm-celda"
                            :style="{ background: sem[d].color }"
                            :title="`${fechaLarga(sem[d].fecha)}: ${euros(sem[d].total)}`"
                            :aria-label="`${fechaLarga(sem[d].fecha)}: ${euros(sem[d].total)}`"
                            @click="irADia(sem[d].fecha)"
                          ></button>
                          <span v-else class="hm-vacia"></span>
                        </template>
                      </template>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-1 small text-muted mt-2 justify-content-end">
                    Menos <span v-for="c in RAMPA" :key="c" class="hm-ley" :style="{ background: c }"></span> Más
                  </div>
                </template>
                <p v-else class="small text-muted">El calendario se muestra para periodos de hasta un año.</p>

                <h3 class="h6 mt-3">¿Qué día {{ datos.tipo === 'gasto' ? 'gastas' : 'ingresas' }} más?</h3>
                <div class="semana">
                  <div v-for="(d, i) in datos.porDiaSemana" :key="d.nombre" class="semana-col" :title="`${d.nombre}: ${euros(d.media)} de media`">
                    <span class="semana-valor small">{{ euroCorto(d.media) }}</span>
                    <span class="semana-barra-fondo">
                      <span class="semana-barra" :class="{ top: i === diaTop }" :style="{ height: `${maxSemana ? (d.media / maxSemana) * 100 : 0}%`, background: i === diaTop ? colorModo : '#86b6ef' }"></span>
                    </span>
                    <span class="small text-muted">{{ d.nombre.slice(0, 3) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <h2 class="h6">{{ datos.tipo === 'gasto' ? 'Dónde más gastas' : 'De dónde entra' }}</h2>
                <p v-if="!datos.topComercios.length" class="text-muted small mb-0">Sin datos.</p>
                <ol class="list-unstyled mb-0 small ranking">
                  <li v-for="(c, i) in datos.topComercios" :key="c.nombre" class="d-flex align-items-center gap-2 py-1">
                    <span class="puesto">{{ i + 1 }}</span>
                    <button type="button" class="btn btn-link btn-sm p-0 text-body text-start text-truncate flex-grow-1" :title="`Filtrar por ${c.nombre}`" @click="buscar(c.nombre)">{{ c.nombre }}</button>
                    <span class="text-muted">{{ c.n }}×</span>
                    <strong class="text-nowrap">{{ euros(c.total) }}</strong>
                  </li>
                </ol>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <h2 class="h6">Los más grandes</h2>
                <p v-if="!datos.mayores.length" class="text-muted small mb-0">Sin datos.</p>
                <ul class="list-unstyled mb-0 small">
                  <li v-for="m in datos.mayores" :key="m.id" class="d-flex align-items-center gap-2 py-1 border-bottom">
                    <span class="fecha-badge">{{ diaMes(m.fecha) }}</span>
                    <span class="flex-grow-1 text-truncate">
                      {{ m.beneficiario || m.nota || '—' }}
                      <span class="d-block text-muted"><span class="dot" :style="{ background: m.categoria_color || '#adb5bd' }"></span>{{ m.categoria_nombre }}</span>
                    </span>
                    <strong class="text-nowrap">{{ euros(m.valor) }}</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Movimientos -->
        <div class="card border-0 shadow-sm">
          <div class="card-body pb-0 d-flex justify-content-between align-items-center">
            <h2 class="h6 mb-0">{{ datos.totales.n }} movimientos</h2>
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="!datos.movimientos.length" @click="exportarCsv">
              <i class="bi bi-download me-1" aria-hidden="true"></i>Exportar CSV
            </button>
          </div>
          <div class="table-responsive mt-2 movs-scroll">
            <table class="table table-sm table-hover mb-0 small align-middle">
              <thead><tr><th scope="col">Fecha</th><th scope="col">Comercio / nota</th><th scope="col">Categoría</th><th scope="col">Cuenta</th><th scope="col" class="text-end">Importe</th></tr></thead>
              <tbody>
                <tr v-if="!datos.movimientos.length"><td colspan="5" class="text-center text-muted py-4">No hay movimientos con estos filtros.</td></tr>
                <tr v-for="m in datos.movimientos.slice(0, verMovs)" :key="m.id">
                  <td class="text-nowrap">{{ diaMes(m.fecha) }}</td>
                  <td>{{ m.beneficiario || '—' }}<div v-if="m.nota" class="text-muted text-truncate mov-nota">{{ m.nota }}</div></td>
                  <td class="text-nowrap"><span class="dot" :style="{ background: m.categoria_color || '#adb5bd' }"></span>{{ m.categoria_nombre }}</td>
                  <td class="text-muted">{{ m.cuenta_nombre }}</td>
                  <td class="text-end text-nowrap fw-semibold" :class="datos.tipo === 'ingreso' ? 'text-success' : ''">{{ euros(m.valor) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="datos.movimientos.length > verMovs" class="card-body pt-2 text-center">
            <button type="button" class="btn btn-link btn-sm" @click="verMovs += 50">Ver más</button>
          </div>
          <p v-if="datos.movimientosTruncados" class="small text-muted px-3 pb-2 mb-0">Se muestran los 500 más recientes; acota el periodo para ver el resto.</p>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS, BarElement, LineElement, LineController, PointElement, CategoryScale, LinearScale, Tooltip, Legend,
} from 'chart.js';
import axios from '../../config/axios';

// El periodo anterior es una línea dentro del gráfico de barras: necesita LineController
ChartJS.register(BarElement, LineElement, LineController, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const STORAGE_KEY = 'wallet-explorar-v1';
// Paleta categórica validada: slot 1 azul (ingresos), slot 2 naranja (gastos)
const COLORES = { gasto: '#eb6834', ingreso: '#2a78d6' };
const COLOR_ANTERIOR = '#8a8984';
// Rampa secuencial azul (100→700) para el calendario; gris = sin movimientos
const RAMPA = ['#f0efec', '#b7d3f6', '#86b6ef', '#3987e5', '#1c5cab', '#0d366b'];
const PRESETS = [
  { id: 'mes', label: 'Este mes' },
  { id: 'mesPasado', label: 'Mes pasado' },
  { id: '3m', label: '3 meses' },
  { id: 'ano', label: 'Este año' },
  { id: 'anoPasado', label: 'Año pasado' },
  { id: '12m', label: '12 meses' },
];

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parse(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function rangoPreset(id, hoy = new Date()) {
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  switch (id) {
    case 'mesPasado': return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
    case '3m': return [iso(new Date(y, m - 2, 1)), iso(hoy)];
    case 'ano': return [iso(new Date(y, 0, 1)), iso(hoy)];
    case 'anoPasado': return [iso(new Date(y - 1, 0, 1)), iso(new Date(y - 1, 11, 31))];
    case '12m': return [iso(new Date(y, m - 11, 1)), iso(hoy)];
    default: return [iso(new Date(y, m, 1)), iso(hoy)];
  }
}

function leerGuardado() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

export default {
  name: 'WalletExplorar',
  components: { Bar },
  props: {
    cuentas: { type: Array, default: () => [] },
    divisa: { type: String, default: 'EUR' },
    // { categoria, desde, hasta, tipo } para llegar desde otras pestañas
    foco: { type: Object, default: null },
  },
  setup(props) {
    const guardado = leerGuardado();
    const [d0, h0] = rangoPreset(guardado?.preset && guardado.preset !== 'custom' ? guardado.preset : 'mes');
    const f = ref({
      tipo: guardado?.tipo || 'gasto',
      preset: guardado?.preset || 'mes',
      desde: guardado?.preset === 'custom' ? guardado.desde : d0,
      hasta: guardado?.preset === 'custom' ? guardado.hasta : h0,
      categorias: guardado?.categorias || [],
      cuentas: guardado?.cuentas || [],
      q: guardado?.q || '',
      min: guardado?.min ?? '',
      max: guardado?.max ?? '',
    });
    const qInput = ref(f.value.q);
    const datos = ref(null);
    const cargando = ref(false);
    const error = ref('');
    const verTodasCats = ref(false);
    const verMovs = ref(50);
    const cuentasAbierto = ref(false);
    let peticion = 0;

    const colorModo = computed(() => COLORES[f.value.tipo]);
    const cuentasVisibles = computed(() => props.cuentas.filter((c) => !c.archivada || f.value.cuentas.includes(c.id)));
    const hayFiltroCat = computed(() => f.value.categorias.length > 0);

    function euros(v) {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: props.divisa || 'EUR', useGrouping: 'always' }).format(Number(v) || 0);
    }
    function euroCorto(v) {
      const n = Number(v) || 0;
      return n >= 1000 ? `${(n / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })}k` : Math.round(n).toString();
    }
    const fechaLarga = (s) => parse(s.slice(0, 10)).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    const diaMes = (s) => parse(String(s).slice(0, 10)).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    const fechaCorta = (s) => parse(s).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: '2-digit' });
    const rangoTexto = computed(() => (datos.value ? `${fechaCorta(datos.value.rango.desde)} – ${fechaCorta(datos.value.rango.hasta)}` : ''));
    const rangoAnteriorTexto = computed(() =>
      datos.value ? `${fechaCorta(datos.value.rango.anterior.desde)} – ${fechaCorta(datos.value.rango.anterior.hasta)}` : ''
    );
    // Más gasto = malo; más ingreso = bueno. Siempre con flecha y texto, no solo color.
    const claseDelta = (pct) => ((pct > 0) === (f.value.tipo === 'gasto') ? 'text-danger' : 'text-success');
    const claseVariacion = computed(() => {
      const pct = datos.value?.totales.variacionPct || 0;
      return (pct > 0) === (f.value.tipo === 'gasto') ? 'variacion-mala' : 'variacion-buena';
    });

    const etiquetaCubo = (clave, gran) => {
      if (gran === 'mes') {
        const [y, m] = clave.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      }
      return parse(clave).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    };

    const chartData = computed(() => {
      const d = datos.value;
      if (!d) return { labels: [], datasets: [] };
      return {
        labels: d.serie.map((s) => etiquetaCubo(s.clave, d.granularidad)),
        datasets: [
          {
            type: 'bar',
            label: d.tipo === 'gasto' ? 'Gastos' : 'Ingresos',
            data: d.serie.map((s) => s.actual),
            backgroundColor: colorModo.value,
            borderRadius: 4,
            borderSkipped: 'start',
            maxBarThickness: 28,
            order: 2,
          },
          {
            type: 'line',
            label: 'Periodo anterior',
            data: d.serie.map((s) => s.anterior),
            borderColor: COLOR_ANTERIOR,
            backgroundColor: COLOR_ANTERIOR,
            borderWidth: 2,
            borderDash: [4, 4],
            pointRadius: d.serie.length > 40 ? 0 : 3,
            pointHoverRadius: 5,
            tension: 0.25,
            order: 1,
          },
        ],
      };
    });

    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      onClick: (_evt, elements) => {
        if (elements?.length) irACubo(elements[0].index);
      },
      onHover: (evt, elements) => {
        if (evt?.native?.target) evt.native.target.style.cursor = elements?.length ? 'pointer' : 'default';
      },
      plugins: {
        legend: {
          position: 'top',
          align: 'start',
          labels: { boxWidth: 10, boxHeight: 10, sort: (a, b) => a.datasetIndex - b.datasetIndex },
        },
        tooltip: {
          itemSort: (a, b) => a.datasetIndex - b.datasetIndex,
          callbacks: { label: (ctx) => `${ctx.dataset.label}: ${euros(ctx.parsed.y)}` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { callback: (v) => euroCorto(v) + ' €' } },
      },
    }));

    // Calendario: columnas = semanas (lunes a domingo); color por cuantiles de los días con importe
    const calendario = computed(() => {
      const d = datos.value;
      if (!d || d.diario.length > 371) return null;
      const valores = d.diario.map((x) => x.total).filter((v) => v > 0).sort((a, b) => a - b);
      const cuantil = (p) => valores[Math.min(valores.length - 1, Math.floor(p * valores.length))];
      const cortes = valores.length ? [0.2, 0.4, 0.6, 0.8].map(cuantil) : [];
      const color = (v) => {
        if (!v) return RAMPA[0];
        let i = 1;
        while (i <= cortes.length && v > cortes[i - 1]) i += 1;
        return RAMPA[Math.min(i, RAMPA.length - 1)];
      };
      const primero = parse(d.diario[0].fecha);
      const offset = (primero.getDay() + 6) % 7;
      const semanas = [];
      d.diario.forEach((x, i) => {
        const pos = i + offset;
        const w = Math.floor(pos / 7);
        if (!semanas[w]) semanas[w] = Array(7).fill(null);
        semanas[w][pos % 7] = { ...x, color: color(x.total) };
      });
      return { semanas };
    });

    const maxSemana = computed(() => Math.max(0, ...(datos.value?.porDiaSemana || []).map((d) => d.media)));
    const diaTop = computed(() => {
      const arr = datos.value?.porDiaSemana || [];
      return arr.reduce((best, d, i) => (d.media > (arr[best]?.media || 0) ? i : best), 0);
    });

    const chipsActivos = computed(() => {
      const out = [];
      const cats = datos.value?.porCategoria || [];
      for (const id of f.value.categorias) {
        const c = cats.find((x) => x.id === id);
        out.push({ key: `cat-${id}`, label: c?.nombre || 'Categoría', color: c?.color, quitar: () => toggleCategoria(id) });
      }
      for (const id of f.value.cuentas) {
        const c = props.cuentas.find((x) => x.id === id);
        out.push({ key: `cu-${id}`, label: c?.nombre || 'Cuenta', color: c?.color, quitar: () => toggleCuenta(id) });
      }
      if (f.value.q) out.push({ key: 'q', label: `«${f.value.q}»`, quitar: () => buscar('') });
      if (f.value.min !== '' && f.value.min != null) out.push({ key: 'min', label: `≥ ${euros(f.value.min)}`, quitar: () => { f.value.min = ''; cargar(); } });
      if (f.value.max !== '' && f.value.max != null) out.push({ key: 'max', label: `≤ ${euros(f.value.max)}`, quitar: () => { f.value.max = ''; cargar(); } });
      return out;
    });

    function guardar() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(f.value));
      } catch {
        /* sin almacenamiento: no pasa nada */
      }
    }

    async function cargar() {
      const id = ++peticion;
      cargando.value = true;
      error.value = '';
      guardar();
      try {
        const params = { tipo: f.value.tipo, desde: f.value.desde, hasta: f.value.hasta };
        if (f.value.categorias.length) params.categorias = f.value.categorias.join(',');
        if (f.value.cuentas.length) params.cuentas = f.value.cuentas.join(',');
        if (f.value.q) params.q = f.value.q;
        if (f.value.min !== '' && f.value.min != null) params.min = f.value.min;
        if (f.value.max !== '' && f.value.max != null) params.max = f.value.max;
        const { data } = await axios.get('/api/wallet/explorar', { params });
        if (id !== peticion) return; // llegó una respuesta más nueva
        datos.value = data;
        verMovs.value = 50;
      } catch (e) {
        if (id === peticion) error.value = e?.response?.data?.error || e.message || 'No se pudo cargar';
      } finally {
        if (id === peticion) cargando.value = false;
      }
    }

    function setTipo(t) {
      if (f.value.tipo === t) return;
      f.value.tipo = t;
      f.value.categorias = [];
      cargar();
    }
    function aplicarPreset(id) {
      const [d, h] = rangoPreset(id);
      Object.assign(f.value, { preset: id, desde: d, hasta: h });
      cargar();
    }
    function manual() {
      if (!f.value.desde || !f.value.hasta || f.value.desde > f.value.hasta) return;
      f.value.preset = 'custom';
      cargar();
    }
    /** Mueve el rango hacia atrás/adelante: meses completos si el rango lo es; si no, su duración. */
    function desplazar(dir) {
      const d = parse(f.value.desde);
      const h = parse(f.value.hasta);
      const finDeMes = new Date(h.getFullYear(), h.getMonth() + 1, 0).getDate() === h.getDate();
      const hoyIso = iso(new Date());
      if (d.getDate() === 1 && (finDeMes || f.value.hasta === hoyIso)) {
        const meses = (h.getFullYear() - d.getFullYear()) * 12 + h.getMonth() - d.getMonth() + 1;
        const nd = new Date(d.getFullYear(), d.getMonth() + dir * meses, 1);
        const nh = new Date(nd.getFullYear(), nd.getMonth() + meses, 0);
        Object.assign(f.value, { desde: iso(nd), hasta: iso(nh) });
      } else {
        const dias = Math.round((h - d) / 86400000) + 1;
        Object.assign(f.value, {
          desde: iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() + dir * dias)),
          hasta: iso(new Date(h.getFullYear(), h.getMonth(), h.getDate() + dir * dias)),
        });
      }
      f.value.preset = 'custom';
      cargar();
    }
    function irACubo(i) {
      const d = datos.value;
      const s = d?.serie[i];
      if (!s) return;
      if (d.granularidad === 'dia') return irADia(s.clave);
      if (d.granularidad === 'semana') {
        const ini = parse(s.clave);
        Object.assign(f.value, { desde: s.clave, hasta: iso(new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + 6)) });
      } else {
        const [y, m] = s.clave.split('-').map(Number);
        Object.assign(f.value, { desde: iso(new Date(y, m - 1, 1)), hasta: iso(new Date(y, m, 0)) });
      }
      f.value.preset = 'custom';
      cargar();
    }
    function irADia(fecha) {
      Object.assign(f.value, { desde: fecha, hasta: fecha, preset: 'custom' });
      cargar();
    }
    function toggleCategoria(id) {
      const set = new Set(f.value.categorias);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      f.value.categorias = [...set];
      cargar();
    }
    function toggleCuenta(id) {
      const set = new Set(f.value.cuentas);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      f.value.cuentas = [...set];
      cargar();
    }
    function buscar(texto) {
      qInput.value = texto;
      aplicarBusqueda();
    }
    function aplicarBusqueda() {
      if (f.value.q === qInput.value) return;
      f.value.q = qInput.value;
      cargar();
    }
    function limpiar() {
      Object.assign(f.value, { categorias: [], cuentas: [], q: '', min: '', max: '' });
      qInput.value = '';
      cargar();
    }

    function exportarCsv() {
      const d = datos.value;
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const filas = [['Fecha', 'Comercio', 'Nota', 'Categoría', 'Cuenta', 'Importe', 'Divisa original', 'Importe original']];
      for (const m of d.movimientos) {
        filas.push([
          String(m.fecha).slice(0, 10), m.beneficiario, m.nota, m.categoria_nombre, m.cuenta_nombre,
          (d.tipo === 'gasto' ? -m.valor : m.valor).toFixed(2).replace('.', ','), m.divisa, String(m.importe ?? '').replace('.', ','),
        ]);
      }
      // BOM + «;» para que Excel en español lo abra bien
      const csv = '﻿' + filas.map((r) => r.map(esc).join(';')).join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `wallet-${d.tipo === 'gasto' ? 'gastos' : 'ingresos'}-${d.rango.desde}_${d.rango.hasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }

    watch(
      () => props.foco,
      (foco) => {
        if (!foco) return;
        if (foco.tipo) f.value.tipo = foco.tipo;
        if (foco.desde && foco.hasta) Object.assign(f.value, { desde: foco.desde, hasta: foco.hasta, preset: 'custom' });
        f.value.categorias = foco.categoria ? [foco.categoria] : [];
        cargar();
      }
    );

    onMounted(() => {
      if (!props.foco) cargar();
      else {
        const foco = props.foco;
        if (foco.tipo) f.value.tipo = foco.tipo;
        if (foco.desde && foco.hasta) Object.assign(f.value, { desde: foco.desde, hasta: foco.hasta, preset: 'custom' });
        f.value.categorias = foco.categoria ? [foco.categoria] : [];
        cargar();
      }
    });

    return {
      PRESETS, RAMPA, f, qInput, datos, cargando, error, verTodasCats, verMovs, cuentasAbierto, colorModo,
      cuentasVisibles, hayFiltroCat, euros, euroCorto, fechaLarga, diaMes, rangoTexto, rangoAnteriorTexto,
      claseDelta, claseVariacion, chartData, chartOptions, calendario, maxSemana, diaTop, chipsActivos, cargar,
      setTipo, aplicarPreset, manual, desplazar, irADia, toggleCategoria, toggleCuenta, buscar, aplicarBusqueda,
      limpiar, exportarCsv,
    };
  },
};
</script>

<style scoped>
/* En móvil la barra ocuparía media pantalla: solo fija en pantallas medianas o más */
@media (min-width: 768px) {
  .filtros {
    position: sticky;
    top: 0.5rem;
    z-index: 5;
  }
}

.btn-gasto {
  background: #eb6834;
  border-color: #eb6834;
  color: #fff;
}

.btn-ingreso {
  background: #2a78d6;
  border-color: #2a78d6;
  color: #fff;
}

.presets {
  overflow-x: auto;
  scrollbar-width: none;
  max-width: 100%;
}

.chip {
  border: 1px solid #dee2e6;
  border-radius: 999px;
  background: #fff;
  white-space: nowrap;
  font-size: 0.8rem;
  padding: 0.2rem 0.7rem;
}

.chip.activo {
  background: #212529;
  border-color: #212529;
  color: #fff;
}

.rango input[type='date'] {
  width: 8.6rem;
}

.buscador {
  min-width: 12rem;
}

.buscador .bi-search {
  left: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
}

.importes input {
  width: 5.5rem;
}

.desplegable {
  position: relative;
}

.desplegable summary {
  list-style: none;
}

.desplegable summary::-webkit-details-marker {
  display: none;
}

.desplegable-menu {
  position: absolute;
  z-index: 10;
  background: #fff;
  border-radius: 0.5rem;
  padding: 0.6rem 0.8rem;
  min-width: 15rem;
  max-height: 18rem;
  overflow: auto;
  margin-top: 0.25rem;
}

.chip-activo {
  background: #e9ecef;
  color: #212529;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.5rem;
}

.btn-close-sm {
  width: 0.5rem;
  height: 0.5rem;
  background-size: 0.5rem;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.35rem;
  vertical-align: middle;
}

.transicion {
  transition: opacity 0.15s;
}

.hero {
  border-left: 4px solid #eb6834 !important;
}

.hero-ingreso {
  border-left-color: #2a78d6 !important;
}

.hero-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  font-weight: 600;
}

.hero-total {
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.15;
  margin: 0.2rem 0 0.4rem;
  font-variant-numeric: tabular-nums;
}

.variacion {
  font-weight: 600;
  padding: 0.35rem 0.55rem;
}

.variacion-mala {
  background: #f8d7da;
  color: #842029;
}

.variacion-buena {
  background: #d1e7dd;
  color: #0f5132;
}

.resumen {
  font-size: 0.875rem;
  color: #495057;
}

.mini .card-body {
  padding: 0.8rem 1rem;
}

.mini-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  font-weight: 600;
}

.mini-valor {
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.mini-sub {
  font-size: 0.7rem;
  color: #6c757d;
}

.chart-box {
  position: relative;
  height: 260px;
  margin-top: 0.5rem;
}

.cat-fila {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 0.35rem 0.4rem;
  border-radius: 0.4rem;
  transition: background 0.1s, opacity 0.1s;
}

.cat-fila:hover {
  background: #f8f9fa;
}

.cat-fila.seleccionada {
  background: #eef4fd;
  box-shadow: inset 3px 0 0 #2a78d6;
}

.cat-fila.apagada {
  opacity: 0.55;
}

.barra-fondo {
  display: block;
  height: 6px;
  border-radius: 3px;
  background: #f0efec;
  margin-top: 4px;
  overflow: hidden;
}

.barra {
  display: block;
  height: 100%;
  border-radius: 3px;
}

.heatmap-scroll {
  overflow-x: auto;
}

.heatmap {
  display: grid;
  gap: 3px;
  min-width: min-content;
}

.hm-dia {
  font-size: 0.65rem;
  color: #6c757d;
  line-height: 1;
  align-self: center;
}

.hm-celda,
.hm-vacia {
  aspect-ratio: 1;
  min-width: 10px;
  max-width: 28px;
  border-radius: 3px;
  border: 0;
  padding: 0;
}

.hm-celda:hover,
.hm-celda:focus-visible {
  outline: 2px solid #212529;
  outline-offset: 1px;
}

.hm-ley {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.semana {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  height: 120px;
}

.semana-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.semana-valor {
  color: #495057;
  font-variant-numeric: tabular-nums;
}

.semana-barra-fondo {
  flex: 1;
  width: 100%;
  max-width: 36px;
  display: flex;
  align-items: flex-end;
}

.semana-barra {
  width: 100%;
  border-radius: 4px 4px 0 0;
  min-height: 2px;
}

.puesto {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: #f0efec;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.fecha-badge {
  background: #f0efec;
  border-radius: 0.35rem;
  padding: 0.15rem 0.4rem;
  font-size: 0.7rem;
  white-space: nowrap;
}

.movs-scroll {
  max-height: 520px;
  overflow: auto;
}

.movs-scroll thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}

.mov-nota {
  max-width: 280px;
}

@media (max-width: 575.98px) {
  .hero-total {
    font-size: 1.9rem;
  }

  .rango {
    width: 100%;
  }

  .rango input[type='date'] {
    flex: 1;
    width: auto;
  }
}
</style>
